
import React from "react";
import { InfoBox } from "@/components/ui/info-box";
import { 
  BarChart2, 
  Briefcase,
  Database,
  Users,
  DollarSign,
  Rocket,
  TrendingUp,
  LineChart
} from "lucide-react";

interface BusinessInfoBoxesProps {
  className?: string;
  customTitles?: string[];
  detailedContent?: boolean;
}

const BusinessInfoBoxes: React.FC<BusinessInfoBoxesProps> = ({ className, customTitles, detailedContent = false }) => {
  // Simple content for default display
  const defaultInfoBoxes = [
    {
      title: "Revenue Management",
      content: "Pricing and bundling optimization, sales & marketing buildout, and expansion across segments and markets.",
      icon: DollarSign
    },
    {
      title: "Product & Innovation",
      content: "Build toward an all-in-one vertical solution through module expansion, embedded finance, and API monetization.",
      icon: Rocket
    },
    {
      title: "Technology, Data & AI",
      content: "Develop scalable data infrastructure, enable workflow automation, and accelerate product add-on deployment.",
      icon: Database
    },
    {
      title: "Financial Operations",
      content: "Standardize reporting, enhance visibility, and improve profitability through cost control and working capital efficiency.",
      icon: LineChart
    },
    {
      title: "People & Culture",
      content: "Streamline org design, automate repetitive tasks, recruit key talent, and preserve company culture through growth.",
      icon: Users
    },
    {
      title: "Strategic M&A",
      content: "Pursue bolt-on acquisitions to grow market share, expand into adjacent products, and enter new customer segments.",
      icon: Briefcase
    }
  ];

  // Detailed content for enhanced display with updated shorter titles
  const detailedInfoBoxes = [
    {
      title: "Revenue Management",
      icon: TrendingUp,
      bulletPoints: [
        "Build GTM team (currently only 1 salesperson; no upsell engine)",
        "Expand into motorbike dealers, repair shops, and Central America",
        "Raise DMS pricing (~15% below peers); monetize API integrations"
      ]
    },
    {
      title: "Product Innovation",
      icon: Rocket,
      bulletPoints: [
        "Bundle cloud hosting to lower costs and boost margins",
        "Replace payment processors with proprietary solution",
        "Insource invoicing module to save $200K+ annually"
      ]
    },
    {
      title: "Technology, Data & AI",
      icon: Database,
      bulletPoints: [
        "Launch cloud-native DMS (V2) to modernize core product (in development since 2023)",
        "Build data lake (20+ years of records) to unlock analytics & AI",
        "Enable AI features for internal efficiency and client-facing tools"
      ]
    },
    {
      title: "Financial Operations",
      icon: BarChart2,
      bulletPoints: [
        "Standardize financial & KPI reporting",
        "Add customer-level margin and pricing analytics",
        "Automate cashflow forecasting"
      ]
    },
    {
      title: "People & Culture",
      icon: Users,
      bulletPoints: [
        "Reduce 50+ FTEs in support via automation",
        "Streamline 50-person ops team post-cloud migration",
        "Plan CEO transition post-earnout"
      ]
    },
    {
      title: "Strategic M&A",
      icon: Briefcase,
      bulletPoints: [
        "Mexico's #2 DMS player (~30% share); owner nearing retirement and open to selling",
        "Lending platform used by 28% of clients; seller is open",
        "CRM used by 30% of clients; strong existing relationship"
      ]
    }
  ];

  // Choose which content to display
  const infoBoxes = detailedContent 
    ? detailedInfoBoxes 
    : defaultInfoBoxes;

  // Apply custom titles if provided
  const displayBoxes = customTitles 
    ? infoBoxes.map((box, index) => ({
        ...box,
        title: customTitles[index] || box.title
      }))
    : infoBoxes;

  return (
    <div className={className}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayBoxes.map((box, index) => (
          <InfoBox 
            key={index}
            title={box.title}
            content={box.content}
            bulletPoints={box.bulletPoints}
            // Only show icons when not in detailed mode
            icon={!detailedContent ? box.icon : undefined}
            // Highlight keywords when in detailed mode
            highlightKeywords={detailedContent}
          />
        ))}
      </div>
    </div>
  );
};

export default BusinessInfoBoxes;
